import { useState } from "react";
import { useQuery } from "react-query";
import { Download, FileText, HandCoins, Landmark, Plus, RefreshCw, Search, Timer, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EmptyRows,
  PortalActionButton,
  PortalBadge,
  PortalPageHeader,
  PortalPanel,
  PortalStatCard,
  SummaryLine,
} from "@/components/admin/PortalPrimitives";
import { cn, formatCurrency } from "@/lib/utils";

export default function LoanRepayments() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["loan-repayments-portal"],
    async () => {
      const response = await api.get("/reports/repayments");
      return response.data?.data;
    },
    { staleTime: 30000 }
  );

  const summary = data?.summary || {};
  const repayments = data?.repayments || [];
  const filteredRows = repayments.filter((row: any) => {
    const query = search.trim().toLowerCase();
    const status = String(row.status || "").toLowerCase();
    const matchesSearch = !query || [row.memberName, row.loanId, row.method, row.status].some((value) => String(value || "").toLowerCase().includes(query));
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "overdue" && status.includes("overdue")) ||
      (activeTab === "paid" && status.includes("paid")) ||
      (activeTab === "partial" && status.includes("partial")) ||
      (activeTab === "pending" && !row.verified);
    return matchesSearch && matchesTab;
  });

  const totalOutstanding = Number(summary.unpaidThisMonth || 0) + Number(summary.overdueInstallments || 0);
  const totalRepaid = Number(summary.paidThisMonth || 0) + Number(summary.salaryDeductionRepayments || 0) + Number(summary.manualRepayments || 0);
  const overdueAmount = repayments
    .filter((row: any) => String(row.status || "").toLowerCase().includes("overdue"))
    .reduce((sum: number, row: any) => sum + Math.max(0, Number(row.amount || 0) - Number(row.paid || 0)), 0);

  return (
    <div className="space-y-5 py-4">
      <PortalPageHeader
        title="Loan Repayments"
        description="Record loan repayments made by members and track overdue installments."
        actions={
          <>
            <PortalActionButton variant="outline" onClick={() => refetch()}>
              <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Refresh
            </PortalActionButton>
            <PortalActionButton variant="outline"><FileText className="mr-2 h-4 w-4" /> Repayment Statement</PortalActionButton>
            <PortalActionButton variant="outline"><Download className="mr-2 h-4 w-4" /> Export</PortalActionButton>
            <PortalActionButton><Plus className="mr-2 h-4 w-4" /> Record Repayment</PortalActionButton>
          </>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_290px]">
        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-5">
            <PortalStatCard title="Total Loans Outstanding" value={formatCurrency(totalOutstanding)} subtitle={`${repayments.length} loans tracked`} icon={<Landmark className="h-5 w-5" />} tone="purple" loading={isLoading} />
            <PortalStatCard title="Total Amount Repaid" value={formatCurrency(totalRepaid)} subtitle="Current reporting period" icon={<Wallet className="h-5 w-5" />} tone="blue" loading={isLoading} />
            <PortalStatCard title="Total Overdue Amount" value={formatCurrency(overdueAmount)} subtitle={`${summary.overdueInstallments || 0} overdue installments`} icon={<Timer className="h-5 w-5" />} tone="red" loading={isLoading} />
            <PortalStatCard title="This Month Collection" value={formatCurrency(Number(summary.paidThisMonth || 0))} subtitle="Paid this month" icon={<HandCoins className="h-5 w-5" />} tone="green" loading={isLoading} />
            <PortalStatCard title="Pending Verification" value={summary.pendingVerification || 0} subtitle="Awaiting confirmation" icon={<FileText className="h-5 w-5" />} tone="amber" loading={isLoading} />
          </div>

          <PortalPanel title="Repayment Filters">
            <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px_130px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Search by member name, loan ID or method..." />
              </div>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option>All Status</option>
              </select>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option>All Methods</option>
              </select>
              <Button variant="outline">Reset</Button>
            </div>
          </PortalPanel>

          <div className="flex flex-wrap gap-6 border-b border-slate-200 text-sm font-semibold">
            {[
              ["all", "All Loans"],
              ["pending", "Pending Verification"],
              ["overdue", "Overdue Loans"],
              ["partial", "Partially Paid"],
              ["paid", "Fully Paid"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={cn("border-b-2 px-1 pb-3", activeTab === key ? "border-[#009A44] text-[#009A44]" : "border-transparent text-slate-500")}
              >
                {label}
              </button>
            ))}
          </div>

          <PortalPanel title="Repayment Details">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="px-3 py-3 font-semibold">Loan ID</th>
                    <th className="px-3 py-3 font-semibold">Member</th>
                    <th className="px-3 py-3 text-right font-semibold">Installment</th>
                    <th className="px-3 py-3 font-semibold">Due Date</th>
                    <th className="px-3 py-3 text-right font-semibold">Amount</th>
                    <th className="px-3 py-3 text-right font-semibold">Paid</th>
                    <th className="px-3 py-3 font-semibold">Status</th>
                    <th className="px-3 py-3 font-semibold">Progress</th>
                    <th className="px-3 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                      <tr key={index}><td className="px-3 py-4" colSpan={9}><Skeleton className="h-5 w-full" /></td></tr>
                    ))
                  ) : filteredRows.length === 0 ? (
                    <EmptyRows colSpan={9} message="No repayments found for the selected filters." />
                  ) : (
                    filteredRows.map((row: any, index: number) => {
                      const pct = Number(row.amount || 0) > 0 ? Math.min(100, Math.round((Number(row.paid || 0) / Number(row.amount || 0)) * 100)) : 0;
                      const status = String(row.status || "pending").toLowerCase();
                      return (
                        <tr key={`${row.loanId}-${row.installment}-${index}`} className="hover:bg-slate-50/70">
                          <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-semibold text-slate-800">{row.loanId}</td>
                          <td className="px-3 py-3 font-semibold text-slate-900">{row.memberName}</td>
                          <td className="px-3 py-3 text-right text-slate-700">{row.installment}</td>
                          <td className="px-3 py-3 text-slate-600">{row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "-"}</td>
                          <td className="px-3 py-3 text-right font-semibold">{formatCurrency(Number(row.amount || 0))}</td>
                          <td className="px-3 py-3 text-right font-semibold text-[#009A44]">{formatCurrency(Number(row.paid || 0))}</td>
                          <td className="px-3 py-3"><PortalBadge tone={status.includes("overdue") || status.includes("unpaid") ? "red" : status.includes("paid") ? "green" : "amber"}>{row.status || "Pending"}</PortalBadge></td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                                <div className={cn("h-full rounded-full", pct >= 100 ? "bg-[#009A44]" : pct > 40 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-slate-600">{pct}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right"><Button variant="outline" size="sm">Record Payment</Button></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </PortalPanel>
        </div>

        <div className="space-y-5">
          <PortalPanel title="Repayment Summary">
            <div className="divide-y divide-slate-100">
              <SummaryLine label="Due This Month" value={summary.dueThisMonth || 0} money />
              <SummaryLine label="Paid This Month" value={summary.paidThisMonth || 0} tone="green" money />
              <SummaryLine label="Unpaid This Month" value={summary.unpaidThisMonth || 0} tone="red" money />
              <SummaryLine label="Overdue Installments" value={summary.overdueInstallments || 0} tone="red" />
              <SummaryLine label="Pending Verification" value={summary.pendingVerification || 0} tone="amber" />
            </div>
          </PortalPanel>
          <PortalPanel title="Quick Actions">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start"><Plus className="mr-2 h-4 w-4 text-[#009A44]" /> Record New Payment</Button>
              <Button variant="outline" className="w-full justify-start"><FileText className="mr-2 h-4 w-4 text-[#009A44]" /> Repayment Statement</Button>
              <Button variant="outline" className="w-full justify-start"><Download className="mr-2 h-4 w-4 text-[#009A44]" /> Export Repayment Report</Button>
            </div>
          </PortalPanel>
          <PortalPanel title="Notes">
            <ul className="space-y-2 text-xs text-slate-500">
              <li>Payments are loaded from real repayment report data.</li>
              <li>Overdue rows are highlighted from repayment status.</li>
              <li>Unverified repayments remain in pending verification.</li>
            </ul>
          </PortalPanel>
        </div>
      </div>
    </div>
  );
}
